/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-04-02 17:55:04
 */
/* eslint-disable no-undef */
import React, { useState } from 'react'
import Excel from 'exceljs'
import { Upload, Modal, message } from 'antd'
import type { UploadFile, UploadProps } from 'antd'
import styles from './index.module.scss'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { setLoading } from '../../../actions/main'
import { importUsers } from '../../../actions/group'
import sensors from '../../../utils/sensors'
import { loadScript } from '../../../utils/utils'
import { InboxOutlined } from '@ant-design/icons'

const { Dragger } = Upload

// 腾讯验证码实例类型
interface TencentCaptchaInstance {
  show: () => void
}

// 腾讯验证码回调结果
interface TencentCaptchaResult {
  ret: number
  ticket: string
  randstr: string
}

// 成员数据类型
interface MemberData {
  userName: string
  userMobile: string
  department: string
}

// 失败数据类型
interface FailedDataItem {
  userName: string
  userMobile: string
  department: string
  mobileStatus: number
}

// 组件 Props 类型
interface UploadComponentProps {
  setIsVisibleResults: (visible: boolean) => void
  updateCallback: (members: MemberData[], failedData: FailedDataItem[]) => void
}

// 声明全局 TencentCaptcha
declare const TencentCaptcha: new (
  appId: string,
  callback: (res: TencentCaptchaResult) => void
) => TencentCaptchaInstance

let tencentCaptcha: TencentCaptchaInstance | null = null

const UploadComponent: React.FC<UploadComponentProps> = ({
  setIsVisibleResults,
  updateCallback
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const dispatch = useAppDispatch()
  const groupId = useAppSelector((state) => state.group.groupId)

  const clearPage = () => {
    setFileList([])
  }

  const getCellPlainValue = (value: any): string => {
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString()
    }
    if (typeof value === 'object' && value?.richText) {
      const texts = value.richText.map(
        (richText: { text: string }) => richText.text
      )
      return texts.join('')
    }
    return ''
  }

  const loadTXCaptcha = (callback: (res: TencentCaptchaResult) => void) => {
    const tCaptcha = (res: TencentCaptchaResult) => {
      if (res.ret === 0) {
        callback(res)
      }
    }

    if ((window as any).TencentCaptcha === undefined) {
      loadScript('/TCaptcha.js', () => {
        tencentCaptcha = new TencentCaptcha('196099232', tCaptcha)
        tencentCaptcha?.show()
      })
    } else {
      tencentCaptcha = new TencentCaptcha('196099232', tCaptcha)
      tencentCaptcha?.show()
    }
  }

  const parseExcel = async (file: File) => {
    const workbook = new Excel.Workbook()
    const reader = new FileReader()
    reader.onloadend = function () {
      const arrayBuffer = reader.result as ArrayBuffer
      workbook.xlsx.load(arrayBuffer).then(function (wb) {
        const data: MemberData[] = []
        const sheet = wb.getWorksheet(1)
        if (!sheet) {
          Modal.error({
            title:
              '解析不到文件内的数据，请下载模板，按照模板填写成员信息后提交',
            onOk: clearPage
          })
        } else {
          if (sheet.actualRowCount <= 2) {
            Modal.error({
              title: '表格中无数据，请按照模板填写成员信息后提交。',
              onOk: clearPage
            })
            return false
          }
          if (sheet.actualRowCount > 1002) {
            Modal.error({
              title: '一次最多导入1000个成员！',
              onOk: clearPage
            })
          } else {
            dispatch(setLoading(true))

            wb.worksheets[0].eachRow(function (row, rowNumber) {
              if (rowNumber > 2) {
                let userName = row.values[1]
                  ? getCellPlainValue(row.values[1])
                  : ''
                let userMobile = row.values[2]
                  ? getCellPlainValue(row.values[2])
                  : ''
                let department = row.values[3]
                  ? getCellPlainValue(row.values[3])
                  : ''

                userName = userName.trim().replace(/\n/g, '')
                userMobile = userMobile.trim().replace(/\n/g, '')
                department = department.trim().replace(/\n/g, '')

                data.push({
                  userName,
                  userMobile,
                  department
                })
              }
            })

            dispatch(setLoading(false))

            if (data.length > 1000) {
              Modal.error({ title: '一次最多导入1000个成员！' })
              return
            }

            loadTXCaptcha((res) => {
              console.log(res)

              dispatch(setLoading(true))
              setIsVisibleResults(false)

              dispatch(
                importUsers({
                  groupID: groupId,
                  ticket: res.ticket,
                  randStr: res.randstr,
                  mobiles: data
                })
              )
                .then((result: any) => {
                  const failedNum = result.mobiles.length
                  const succeedNum = data.length - failedNum

                  sensors.track(
                    'web_workgroup_membermanagement_addmember_Excel_upload',
                    {
                      members: data.length,
                      succeedNum,
                      failedNum,
                      from: 'organization'
                    }
                  )

                  updateCallback(data, result.mobiles)

                  dispatch(setLoading(false))
                  setIsVisibleResults(true)
                })
                .catch(() => {
                  dispatch(setLoading(false))
                })
            })
          }
        }
      })
    }
    reader.readAsArrayBuffer(file)
  }

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isExcelFile =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application.vnd.ms-excel'

    if (!isExcelFile) {
      message.error('文件格式错误，仅支持xls、xlsx文件！')
    } else if (file.size / 1024 / 1024 > 100) {
      message.error('上传文件过大，文件最大支持100MB！')
    } else {
      setFileList([file as any])
      parseExcel(file as File)
    }
    return false
  }

  const uploadProps: UploadProps = {
    accept:
      'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    name: 'file',
    multiple: false,
    beforeUpload,
    onRemove: clearPage,
    fileList
  }

  return (
    <Dragger {...uploadProps}>
      <div className={styles['upload-drag-wrapper']}>
        <InboxOutlined
          style={{
            fontSize: 48,
            color: '#1890FF'
          }}
        />
        <p className={styles['upload-text']}>
          点击选择文件或将文件拖拽至此处上传
        </p>
        <p className={styles['upload-hint']}>仅支持xls、xlsx格式文件</p>
      </div>
    </Dragger>
  )
}

export default UploadComponent
